import clsx from 'clsx'
import React, { memo, useContext, useMemo } from 'react'

import SvgBag2 from '@Assets/SVG/Bag2'
import SvgHear2 from '@Assets/SVG/Hear2'
import SvgSearch2 from '@Assets/SVG/Search2'
import SvgUser2 from '@Assets/SVG/User2'
import { Abundance } from '@Components/Badges'
import { CountryDetectionModal } from '@Components/CountryDetectionModal'
import { LanguageToggle } from '@Components/Header/LanguageToggle'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { NativeModal, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { SideCartContext } from '@Domains/Cart/Context/SideCartContextProvider'
import { GetNumberOfLineItemsInCart } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/CartOverview'
import { useClub } from '@Domains/Club/Hook'
import { Menu } from '@Domains/Navigation/Features/Menu/Menu'
import { TopMenu } from '@Domains/Navigation/Features/Menu/TopMenu'
import { Search } from '@Domains/Navigation/Features/Search/Search'
import { useWishListCount } from '@Domains/WishList/Hooks'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './MainHeader.module.scss'

const CartButton = () => {
  const { t } = useTranslation()
  const { cartSummary, openCart } = useContext(SideCartContext)

  const numberOfProducts: number = useMemo(
    () => GetNumberOfLineItemsInCart(cartSummary?.lineItems || []),
    [cartSummary?.lineItems],
  )

  return (
    <div className={clsx(styles.cart, styles.withClub)}>
      <button
        aria-label={t('header:CART_SHORT_LABEL')}
        className={styles.icon}
        onClick={() => openCart()}
        type="button"
      >
        <Abundance className={clsx(styles.smallAbundance)} count={numberOfProducts}>
          <SvgBag2 />
        </Abundance>
      </button>
    </div>
  )
}

const SearchButton = () => {
  const { t } = useTranslation()
  const topNavV2 = useSplitTest('top_nav_v2', 'b')
  const searchModalActionRef = useNativeModalActions()

  return (
    <div className={clsx(styles.search, styles.withClub)}>
      <button
        aria-label={t('header:WB_SEARCH_LABEL')}
        className={clsx(styles.mobileSearch)}
        onClick={() => {
          searchModalActionRef.current?.open()
          preventBodyScroll()
        }}
        type="button"
      >
        {topNavV2 && (<span>{t('header:WB_SEARCH_LABEL')}</span>)}
        <SvgSearch2 />
      </button>

      <NativeModal
        actionRef={searchModalActionRef}
        alignment={topNavV2 ? 'right' : 'left'}
        backdrop
        className={clsx(styles.searchDialog, topNavV2 && styles.topNavV2SearchDialog, 'searchDialog')}
        closeOnBackDrop
        closeOnEscape
        onClose={() => enableBodyScroll()}
      >
        <NativeModalHeader className={styles.searchDialogClose} float />
        <Search />
      </NativeModal>
    </div>
  )
}

const WishListButton = () => {
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)
  const itemsInWishlist = useWishListCount()

  return (
    <div className={clsx(styles.wish, styles.withClub)}>
      <Link
        analytics={{
          dest_page_type: 'wish-list',
          dest_page_type_id: '',
          source_id: 'header',
        }}
        aria-label={t('header:WISH_LIST_LABEL')}
        className="icon"
        href={storeURL('account/wishlist')}
      >
        <Abundance className={clsx(styles.smallAbundance)} count={itemsInWishlist}>
          <SvgHear2 />
        </Abundance>
      </Link>
    </div>
  )
}

const FrontpageLink = () => {
  const { storeURL } = useContext(MetadataContext)

  return (
    <div className={clsx(styles.logo)}>
      <Link
        analytics={{
          dest_page_type: 'frontpages',
          dest_page_type_id: '',
          source_id: 'header',
        }}
        className="logo"
        href={storeURL('')}
      >
        <ImageV4
          alt="Trendhim"
          src="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt59296545a01439bf/trendhim-logo-with-text-v2.svg"
          width={140}
        />
      </Link>
    </div>
  )
}

const AccountOverviewLink = () => {
  const { storeURL } = useContext(MetadataContext)
  const { isLoggedIn } = useClub()

  return (
    <Link
      analytics={{
        dest_page_type: 'account-overview',
        dest_page_type_id: '',
        source_id: 'header',
      }}
      className={clsx(styles.profile, isLoggedIn && styles.authenticated, styles.withClub)}
      href={storeURL('account/overview')}
    >
      <SvgUser2 />
    </Link>
  )
}

export const MainHeader = memo(() => {
  const topNavV2 = useSplitTest('top_nav_v2', 'b')

  const MainHeaderWrapper: React.ComponentType<React.PropsWithChildren<{}>> = topNavV2
    ? ({ children }) => <div className={styles.mainHeaderWrapper}>{children}</div>
    : React.Fragment

  return (
    <MainHeaderWrapper>
      <div className={clsx(styles.mainHeader, topNavV2 && styles.topNavV2)}>
        <CountryDetectionModal />
        {topNavV2 && <TopMenu className={styles.topmenu} />}
        {/* top_nav_v2: render SearchButton conditionally to ensure correct tabindex positioning */}
        {!topNavV2 && <SearchButton />}
        <FrontpageLink />
        <div className={styles.language}>
          <LanguageToggle showTitle={false} />
        </div>
        {/* top_nav_v2: render SearchButton conditionally to ensure correct tabindex positioning */}
        {topNavV2 && <SearchButton />}
        <AccountOverviewLink />
        <WishListButton />
        <CartButton />
        <div className={styles.line} />
        <div className={styles.menu}>
          <Menu />
        </div>
      </div>
    </MainHeaderWrapper>
  )
})

MainHeader.displayName = 'MainHeader'
